/**
 * Shared modal chrome (extracted from VideoSelectorModal / ImageInsertModal,
 * DUP-5): full-screen dimmed overlay + centered dialog container with focus
 * trap, Escape-to-close, and click-outside-to-close via `useModalFocusTrap`.
 *
 * Renders `null` while closed but must stay mounted (do not conditionally
 * unmount it from the parent) so focus is restored when the modal closes.
 *
 * New or refactored dialogs should use this shell instead of duplicating
 * overlay/dialog markup and focus-trap plumbing.
 */

import { useLayoutEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import { adminTokens } from '../admin-ui';
import { useModalFocusTrap } from './useModalFocusTrap';

/**
 * `admin` frames a dialog opened from a management page (lists, settings,
 * shell) in the admin visual language. Editor invocations keep `default`, so a
 * dialog shared by both looks unchanged inside the editor.
 */
export type ModalPresentation = 'default' | 'admin';

export interface ModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  /** id of the element labelling the dialog (e.g. the title heading). */
  ariaLabelledBy: string;
  /** Dialog width (capped at 90vw). Defaults to '600px'. */
  width?: string;
  /** Dialog corner radius. Defaults to '8px' ('16px' for the admin presentation). */
  borderRadius?: string;
  /** When true, clicking the dimmed backdrop does NOT close the modal (Escape still does). */
  disableBackdropClose?: boolean;
  /** Visual frame only; focus trap, Escape and focus restoration are identical. */
  presentation?: ModalPresentation;
  /** Dialog max height for the admin presentation. Defaults to '90vh'. */
  maxHeight?: string;
  /** A fixed dialog height for the admin presentation (the frame then keeps its size as its content changes). Defaults to the content's height, within `maxHeight`. */
  height?: string;
  /** Center the dialog over the page's <main> content pane (beside the nav rail) instead of the full window. The dim backdrop still covers everything. */
  centerOnContent?: boolean;
  children: ReactNode;
}

export function ModalShell({
  isOpen,
  onClose,
  ariaLabelledBy,
  width = '600px',
  borderRadius,
  disableBackdropClose = false,
  presentation = 'default',
  maxHeight = '90vh',
  height,
  centerOnContent = false,
  children,
}: ModalShellProps) {
  const { tokens } = useTheme();
  const { modalRef, handleKeyDown } = useModalFocusTrap({ isOpen, onClose });
  const styles = useMemo(
    () => (presentation === 'admin'
      ? createAdminStyles(width, borderRadius ?? adminTokens.radius.panel, maxHeight, height)
      : createStyles(tokens, width, borderRadius ?? '8px')),
    [tokens, width, borderRadius, presentation, maxHeight, height]
  );

  const [contentInset, setContentInset] = useState<{ left: number; right: number } | null>(null);
  useLayoutEffect(() => {
    if (!isOpen || !centerOnContent) {
      setContentInset(null);
      return undefined;
    }
    const measure = () => {
      const main = document.querySelector('main');
      if (!main) {
        setContentInset(null);
        return;
      }
      const rect = main.getBoundingClientRect();
      setContentInset({ left: Math.max(0, rect.left), right: Math.max(0, window.innerWidth - rect.right) });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isOpen, centerOnContent]);

  if (!isOpen) return null;

  return (
    <div
      style={contentInset ? { ...styles.overlay, boxSizing: 'border-box', paddingLeft: contentInset.left, paddingRight: contentInset.right } : styles.overlay}
      onClick={disableBackdropClose ? undefined : onClose}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby={ariaLabelledBy}
    >
      <div
        ref={modalRef}
        style={styles.modal}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );
}

function createStyles(
  tokens: ReturnType<typeof useTheme>['tokens'],
  width: string,
  borderRadius: string
): Record<string, CSSProperties> {
  return {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: `color-mix(in srgb, ${tokens.backgrounds.inverse} 50%, transparent)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modal: {
      backgroundColor: tokens.backgrounds.page,
      borderRadius,
      width,
      maxWidth: '90vw',
      maxHeight: '90vh',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: tokens.shadow.md,
    },
  };
}

function createAdminStyles(width: string, borderRadius: string, maxHeight: string, height?: string): Record<string, CSSProperties> {
  return {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: adminTokens.surface.scrim,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modal: {
      backgroundColor: adminTokens.surface.panel,
      border: `1px solid ${adminTokens.border.subtle}`,
      borderRadius,
      width,
      maxWidth: '90vw',
      maxHeight,
      ...(height ? { height } : {}),
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: adminTokens.shadow.overlay,
      // No inherited color/font here: a management dialog may still host a
      // shared editor control (e.g. the tag picker), which must keep what it
      // inherits today. Admin leaves set their own text styles.
    },
  };
}
